import Link from 'next/link'
import { useRef, useState, useEffect } from 'react'
import Image from 'next/legacy/image'
import NodeCache from 'node-cache'
import cn from 'classnames'
import { Swiper, SwiperSlide } from 'swiper/react'
// Components
import BasicHeadSeo from '@/components/basicHeadSeo'
import Header from '@/components/newHeader/Header'
import PuppyCard from '@/components/newPuppyCard/puppyCard'
import Card from '@/components/blog/Card'
import Questions from '@/components/blog/Questions'
import Footer from '@/components/newFooter'
import styles from './[slug].module.scss'
// Utils
import { initializeApollo } from '@/utils/apolloClient'
import { GET_POST_DOGS } from '@/graphql/index'
import { getRelatedPosts, getMainCategory } from '@/components/blog/lib/utils'
import { getAllPosts, getPost } from '@/components/blog/lib/cosmic'

import { limitShowPuppies, slugConverter } from '@/utils/converter'
import BlogPost from '@/components/blogPost/blogPost'
import { format, parseISO } from 'date-fns'

export default function Post({ post, relatedPosts, relatedPuppies }) {
  const { categoryTitle, categorySlug } = getMainCategory(post)
  const sharingText = `Check out this post in The woof-paper! ${
    post?.title
  } - ${process.env.NEXT_PUBLIC_ROOT_URL + '/blog/' + post?.slug}`

  // HANDLE NUMBER OF RELATED PUPPIES
  const relatedPuppiesData = limitShowPuppies(post?.content, relatedPuppies)
    .filter((p) => p.isAvailable)
    .sort((a, b) => parseFloat(b.viewScore) - parseFloat(a.viewScore))

  /* Swiper Handlers   */
  const ref = useRef(null)

  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  const goNext = async () => {
    if (ref.current !== null && ref.current.swiper !== null) {
      await ref.current.swiper.slideNext()
      setIsBeginning(ref.current?.swiper?.isBeginning)
      setIsEnd(ref.current?.swiper?.isEnd)
    }
  }

  const goPrev = async () => {
    if (ref.current !== null && ref.current.swiper !== null) {
      await ref.current.swiper.slidePrev()
      setIsBeginning(ref.current?.swiper?.isBeginning)
      setIsEnd(ref.current?.swiper?.isEnd)
    }
  }

  const [showCopy, setShowCopy] = useState(false)

  function copy() {
    const el = document.createElement('input')
    el.value = process.env.NEXT_PUBLIC_ROOT_URL + '/blog/' + post?.slug
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)
    setShowCopy(true)
    return setTimeout(() => {
      setShowCopy(false)
    }, 2000)
  }

  const truncate = (str, n, closing = '') => {
    if (str.length > n - closing.length) {
      return str.slice(0, n - closing.length - 3) + '...' + closing
    }

    return str + closing
  }

  const [hasLoaded, setHasLoaded] = useState(false)
  // Check if Javascript is fully loaded
  useEffect(() => {
    setHasLoaded(true)
  })

  return (
    <>
      <BasicHeadSeo
        title={truncate(post?.title, 60, ' - MawooPets.com')}
        description={truncate(post?.metadata?.seo_description, 150)}
        url={process.env.NEXT_PUBLIC_ROOT_URL + '/blog/' + post?.slug}
        image={`${process.env.NEXT_PUBLIC_MEDIA_BUCKET}${post?.metadata?.image?.imgix_url}`}
        content={
          process.env.NEXT_PUBLIC_ROOT_URL === 'https://www.mawoopets.com'
            ? 'index, follow'
            : 'noindex, nofollow'
        }
      />

      <main className={styles.main}>
        <Header />
        <div className={styles.post}>
          <div className={styles.route}>
            <Link href="/blog" legacyBehavior>
              <div className={styles.breadcrumb}>The Daily Dog</div>
            </Link>
            <div className={styles.icon} />
            <Link href={`/blog?category=${categorySlug}`} legacyBehavior>
              <div className={styles.breadcrumb}>{categoryTitle}</div>
            </Link>
          </div>

          <h1>{post?.title}</h1>

          <div className={styles.signature}>
            <p>
              By {post?.metadata?.author} · {post?.metadata?.reading_time} read
            </p>
            <p>
              Last updated:{' '}
              {post?.modified_at
                ? format(parseISO(post.modified_at), 'MMMM do, yyyy')
                : ''}
            </p>
          </div>

          <div className={styles.image}>
            {post?.metadata?.image?.imgix_url && (
              <Image
                src={post?.metadata?.image?.imgix_url}
                alt={post?.title}
                layout="responsive"
                width={1074}
                height={537}
                priority
              />
            )}
          </div>

          {!!post?.metadata?.epigraph && (
            <div className={styles.epigraph}>{post?.metadata?.epigraph}</div>
          )}

          <div
            className={styles.content}
            style={{ marginTop: post?.metadata?.epigraph ? '22px' : '50px' }}
          >
            <div
              className={styles.text}
              dangerouslySetInnerHTML={{ __html: post?.content }}
            />

            {relatedPuppies?.length ? (
              <div className={cn(styles.sidebar, styles.puppies)}>
                <h2>
                  <Link
                    href={`/puppies-for-sale/${slugConverter(
                      relatedPuppies[0].breed.name.toLowerCase(),
                    )}`}
                  >
                    Available {relatedPuppies[0].breed.name} Puppies
                  </Link>
                </h2>

                {relatedPuppiesData?.map((d) => (
                  <PuppyCard key={d.id} dog={d} />
                ))}
              </div>
            ) : (
              <div className={styles.sidebar}>
                <h2>Related posts</h2>

                {relatedPosts?.map((p) => (
                  <Card
                    key={p.slug}
                    slug={p.slug}
                    title={p.title}
                    image={p.metadata?.image?.imgix_url}
                  />
                ))}
              </div>
            )}
          </div>

          <div className={styles.media}>
            <h4>SHARE THIS ARTICLE</h4>

            <div className={styles.icons}>
              <a href={`https://api.whatsapp.com/send?text=${sharingText}`}>
                <div className={styles.iconWp} />
              </a>

              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${
                  process.env.NEXT_PUBLIC_ROOT_URL + '/blog/' + post?.slug
                }`}
                target="_blank"
                rel="noreferrer"
              >
                <div className={styles.iconFb} />
              </a>

              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${
                  process.env.NEXT_PUBLIC_ROOT_URL + '/blog/' + post?.slug
                }`}
                target="_blank"
                rel="noreferrer"
              >
                <div className={styles.iconLinkedin} />
              </a>

              <a
                href={`https://twitter.com/intent/tweet?text=${sharingText}`}
                target="_blank"
                rel="noreferrer"
              >
                <div className={styles.iconTw} />
              </a>

              <div className={styles.container} id="copy_link">
                {showCopy && <div id="info" className={styles.tooltipInfo} />}
                <button onClick={copy}>
                  <div className={styles.iconLink} />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          className={cn({
            [styles.articles]: true,
            [styles.hidden]: !(relatedPuppies?.length >= 0),
          })}
        >
          <h2>Related articles</h2>

          {hasLoaded ? (
            <div className={styles.cards}>
              <button
                className={cn({
                  [styles.swiperButton]: true,
                  [styles.swiperPrev]: !isBeginning,
                  [styles.disabledPrev]: isBeginning,
                })}
                onClick={goPrev}
              />
              <button
                className={cn({
                  [styles.swiperButton]: true,
                  [styles.swiperNext]: !isEnd,
                  [styles.disabledNext]: isEnd,
                })}
                onClick={goNext}
              />
              <div className={styles.scroll}>
                <div className={styles.cardWrapper}>
                  <Swiper
                    speed={1000}
                    slidesPerView={4}
                    initialSlide={0}
                    grabCursor
                    breakpoints={{
                      1020: {
                        slidesPerView: 4,
                        spaceBetween: 20,
                      },
                      860: {
                        slidesPerView: 3,
                        spaceBetween: 40,
                      },
                      540: {
                        slidesPerView: 2,
                        spaceBetween: 20,
                      },
                      360: {
                        slidesPerView: 'auto',
                        spaceBetween: 10,
                      },
                    }}
                    ref={ref}
                  >
                    {relatedPosts?.map((p, i) => (
                      <SwiperSlide key={i}>
                        <div className={styles.pair}>
                          <Card
                            key={p.slug}
                            slug={p.slug}
                            title={p.title}
                            image={p.metadata?.image?.imgix_url}
                            readingTime={p.metadata?.reading_time}
                            details
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.cards}>
              {' '}
              <BlogPost data={relatedPosts} />
            </div>
          )}
        </div>

        {!!relatedPuppies?.length && (
          <div className={cn(styles.dogs)}>
            <h2>Related puppies for sale</h2>

            <div className={styles.cards}>
              <div className={styles.scroll}>
                <div className={styles.cardWrapper}>
                  <Swiper
                    speed={1000}
                    slidesPerView={4}
                    initialSlide={0}
                    grabCursor
                    breakpoints={{
                      1020: {
                        slidesPerView: 4,
                        spaceBetween: 20,
                      },
                      860: {
                        slidesPerView: 3,
                        spaceBetween: 40,
                      },
                      540: {
                        slidesPerView: 2,
                        spaceBetween: 20,
                      },
                      360: {
                        slidesPerView: 'auto',
                        spaceBetween: 5,
                      },
                    }}
                  >
                    {relatedPuppies?.map((d, i) => (
                      <SwiperSlide key={i}>
                        <div className={styles.pair}>
                          <PuppyCard key={d.id} dog={d} />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>
            </div>
          </div>
        )}

        <Questions />

        <Footer />
      </main>
    </>
  )
}

const cache = new NodeCache({ stdTTL: 60 * 60, checkperiod: 60 }) // One hour

export async function getStaticProps({ params }) {
  const REDIRECT = {
    'when-do-puppies-get-easier-surviving-the-first-six-months':
      'when-do-puppies-get-easier',
    'first-week-with-puppy-9-puppy-tips-for-the-first-week':
      'first-week-with-puppy',
    'lazy-dog-breeds-who-live-for-naptime-and-cuddles': 'lazy-dog-breeds',
    'whats-the-difference-between-adopting-and-buying-a-dog':
      'difference-between-adopting-buying-dog',
    'the-best-emotional-support-and-service-dogs-for-the-elderly':
      'service-dog-for-elderly',
    'the-best-apartment-dogs-for-small-space-living': 'best-apartment-dogs',
    'how-to-make-your-dog-a-service-dog-complete-guide':
      'how-to-make-your-dog-a-service-dog',
    'should-i-get-a-dog-how-to-know-you-are-ready': 'should-i-get-a-dog',
    'dogs-that-can-run-with-you': 'best-dogs-for-runners',
    'finance-a-dog': 'pet-financing-quick-guide',
    '5-ways-puppies-make-you-happier': 'puppies-make-you-happier',
    'should-you-finance-a-dog': 'pet-financing-quick-guide',
    'doodle-comparison': 'doodle-breeds-comparison-guide',
    '5-most-playful-dog-breeds': 'most-playful-dog-breed',
  }
  if (REDIRECT[params.slug]) {
    return {
      redirect: {
        destination: '/blog/' + REDIRECT[params.slug],
        statusCode: 301,
      },
    }
  }

  const posts = await getAllPosts()
  const post = await getPost({
    type: 'posts',
    slug: params.slug,
    content: [
      'slug',
      'title',
      'content',
      'metadata.author',
      'metadata.date',
      'metadata.reading_time',
      'metadata.image',
      'metadata.epigraph',
      'metadata.category.slug',
      'metadata.category.title',
      'metadata.breed_slug',
      'metadata.seo_description',
      'created_at',
      'modified_at',
      'published_at',
    ],
  })

  if (!post.slug) return { notFound: true }

  /* Get related posts */
  const relatedPosts = getRelatedPosts(post, posts)

  /* Get related puppies */
  let relatedPuppies = []
  if (post?.metadata?.breed_slug) {
    relatedPuppies = cache.get(`${post?.metadata?.breed_slug} related puppies`)

    if (relatedPuppies === undefined) {
      try {
        const apolloClient = initializeApollo()

        const { data: postDogs = '' } = await apolloClient.query({
          query: GET_POST_DOGS,
          variables: {
            slug: post?.metadata?.breed_slug,
            limit: 30,
          },
        })
        relatedPuppies = postDogs?.breed?.dogs
        cache.set(
          `${post?.metadata?.breed_slug} related puppies`,
          postDogs?.breed?.dogs,
        )
      } catch (error) {
        console.error('GET_BLOG_RELATED_PUPPIES', error)
      }
    }
  }

  return {
    props: {
      post,
      relatedPosts,
      relatedPuppies: relatedPuppies || [],
    },
    revalidate: 60 * 60 * 12, // Revalidate every 12 hour
  }
}

export async function getStaticPaths() {
  const posts = await getAllPosts()

  return {
    paths: [...posts.map((post) => ({ params: { slug: post.slug } }))],
    fallback: 'blocking',
  }
}
